// Check in (any employee; phones and shop kiosks): "I'm here" / "I'm leaving" with your own PIN
// (jobq-api POST /security/checkin, 2026-10-25: weekend crews who don't clock in). A check-in switches
// the alarm off while you're here, so the PIN is ALWAYS typed: never prefilled, no admin autofill, no
// face sign-in, and it is cleared after every try. The call carries X-Checkin-Pin and the API key, never
// X-Admin-Pin (features/security/doors.js sendCheckin).

import React, { useEffect, useRef, useState } from 'react';
import { PIN_MAX, PIN_MIN, checkinAnswer, cleanPin, pinReady, sendCheckin } from '../features/security/doors';

const ANSWER_SHOWN_MS = 30000; // a shared kiosk doesn't keep the last person's name up for long

const styles = {
  page: { maxWidth: 480, margin: '0 auto', padding: '24px 16px 48px', display: 'grid', gap: 16 },
  title: { fontSize: 28, fontWeight: 700, margin: 0 },
  lead: { fontSize: 16, color: 'var(--bs-secondary-color, #6c757d)', margin: 0 },
  label: { fontSize: 16, fontWeight: 600, display: 'grid', gap: 6 },
  pin: { fontSize: 28, letterSpacing: '0.3em', textAlign: 'center', minHeight: 60 },
  buttons: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 },
  big: { minHeight: 76, fontSize: 20, fontWeight: 700, borderRadius: 12 },
  answer: (tone) => ({
    fontSize: 18, borderRadius: 12, padding: '14px 16px', margin: 0,
    background: tone === 'ok' ? '#d1f2dd' : '#fde2e1', color: tone === 'ok' ? '#14532d' : '#7f1d1d',
    border: `2px solid ${tone === 'ok' ? '#16a34a' : '#dc2626'}`,
  }),
  hint: { fontSize: 13, color: 'var(--bs-secondary-color, #6c757d)', margin: 0 },
};

export default function CheckInPage() {
  const [pin, setPin] = useState('');
  const [busy, setBusy] = useState(null); // 'arrive' | 'leave' while sending
  const [answer, setAnswer] = useState(null);
  const pinRef = useRef(null);
  const timerRef = useRef(null);

  useEffect(() => () => clearTimeout(timerRef.current), []);

  const press = async (event) => {
    if (!pinReady(pin) || busy) return;
    const typed = pin;
    setPin(''); // cleared after every try, whatever the answer
    setBusy(event);
    setAnswer(null);
    clearTimeout(timerRef.current);
    const res = await sendCheckin(event, typed);
    setBusy(null);
    setAnswer(checkinAnswer(res, event));
    timerRef.current = setTimeout(() => setAnswer(null), ANSWER_SHOWN_MS);
  };

  const ready = pinReady(pin) && !busy;
  return (
    <div style={styles.page} data-testid="check-in">
      <h1 style={styles.title}>Check in</h1>
      <p style={styles.lead}>
        Working after hours or on a weekend? Check in so the door alarm and cameras know you're here.
        Check out when you leave.
      </p>
      <label style={styles.label} htmlFor="checkin-pin">
        Your JobQ PIN
        <input
          id="checkin-pin"
          ref={pinRef}
          className="form-control"
          style={styles.pin}
          type="password"
          inputMode="numeric"
          pattern="[0-9]*"
          autoComplete="off"
          name="checkin-pin"
          maxLength={PIN_MAX}
          value={pin}
          disabled={!!busy}
          onChange={(e) => setPin(cleanPin(e.target.value))}
        />
      </label>
      <div style={styles.buttons}>
        <button type="button" className="btn btn-success" style={styles.big} disabled={!ready} onClick={() => press('arrive')}>
          {busy === 'arrive' ? 'Sending…' : "I'm here"}
        </button>
        <button type="button" className="btn btn-outline-dark" style={styles.big} disabled={!ready} onClick={() => press('leave')}>
          {busy === 'leave' ? 'Sending…' : "I'm leaving"}
        </button>
      </div>
      {!answer && <p style={styles.hint}>Type your PIN ({PIN_MIN} to {PIN_MAX} digits), then press a button.</p>}
      {answer && (
        <div role="status" style={styles.answer(answer.tone)} data-testid="checkin-answer">
          {answer.lines.map((l) => <div key={l}>{l}</div>)}
        </div>
      )}
    </div>
  );
}
